import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert, ProgressBar, Table, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faFileCsv, 
  faCheckCircle, 
  faExclamationTriangle,
  faArrowLeft,
  faDownload,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const BulkImport = () => {
  const navigate = useNavigate();
  const { userCompany } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);

  const csvTemplate = [
    {
      groupName: 'Sample Society',
      groupType: 'society',
      groupLocation: 'Dubai Marina',
      unitNumber: 'A-101',
      unitType: 'apartment',
      bedrooms: '2',
      bathrooms: '2',
      area: '1200',
      floor: '10',
      status: 'available',
      description: 'Luxury apartment with marina view'
    }
  ];

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a valid CSV file');
        return;
      }
      
      setFile(selectedFile);
      setError('');
      setSuccess('');
      setPreviewData([]);
      setUploadResults(null);
      
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        const data = [];
        for (let i = 1; i < Math.min(lines.length, 6); i++) {
          if (lines[i].trim()) {
            const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            data.push(row);
          }
        }
        
        setPreviewData(data);
      } catch (error) {
        setError('Error parsing CSV file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const validateData = (data) => {
    const errors = [];
    const requiredFields = ['groupName', 'groupType', 'groupLocation', 'unitNumber', 'unitType'];
    
    // Check if all required headers are present
    const headers = Object.keys(data[0] || {});
    const missingHeaders = requiredFields.filter(field => !headers.includes(field));
    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(', ')}`);
      return errors;
    }
    
    data.forEach((row, index) => {
      requiredFields.forEach(field => {
        if (!row[field] || row[field].trim() === '') {
          errors.push(`Row ${index + 1}: Missing required field "${field}"`);
        }
      });
      
      // Validate group type
      if (row.groupType && !['tower', 'community'].includes(row.groupType.toLowerCase())) {
        errors.push(`Row ${index + 1}: Invalid group type. Must be tower or community`);
      }
      
      // Validate unit type
      if (row.unitType && !['apartment', 'villa', 'penthouse', 'studio', 'townhouse'].includes(row.unitType.toLowerCase())) {
        errors.push(`Row ${index + 1}: Invalid unit type. Must be apartment, villa, penthouse, studio, or townhouse`);
      }
      
      // Validate status
      if (row.status && !['available', 'occupied', 'maintenance', 'reserved'].includes(row.status.toLowerCase())) {
        errors.push(`Row ${index + 1}: Invalid status. Must be available, occupied, maintenance, or reserved`);
      }
    });
    
    return errors;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              data.push(row);
            }
          }

          const validationErrors = validateData(data);
          if (validationErrors.length > 0) {
            setError(`Validation errors:\n${validationErrors.join('\n')}`);
            setLoading(false);
            return;
          }

          setUploadProgress(10);

          const results = {
            total: data.length,
            successful: 0,
            failed: 0,
            errors: []
          };

          const groupedData = {};
          data.forEach(row => {
            const groupKey = `${row.groupName}-${row.groupType}-${row.groupLocation}`;
            if (!groupedData[groupKey]) {
              groupedData[groupKey] = {
                group: {
                  name: row.groupName,
                  type: row.groupType.toLowerCase(),
                  location: row.groupLocation,
                  companyId: userCompany
                },
                units: []
              };
            }
            groupedData[groupKey].units.push({
              unitNumber: row.unitNumber,
              unitType: row.unitType.toLowerCase(),
              bedrooms: parseInt(row.bedrooms) || 0,
              bathrooms: parseInt(row.bathrooms) || 0,
              area: parseFloat(row.area) || 0,
              floor: parseInt(row.floor) || 0,
              status: row.status.toLowerCase() || 'available',
              description: row.description || '',
              companyId: userCompany
            });
          });

          setUploadProgress(30);

          let processed = 0;
          for (const [groupKey, groupData] of Object.entries(groupedData)) {
            try {
              const createdGroup = await propertyService.createPropertyGroup(groupData.group);
              setUploadProgress(30 + (processed / Object.keys(groupedData).length) * 40);

              for (const unit of groupData.units) {
                try {
                  await propertyService.createPropertyUnit({
                    ...unit,
                    groupId: createdGroup.id
                  });
                  results.successful++;
                } catch (unitError) {
                  results.failed++;
                  results.errors.push(`Failed to create unit ${unit.unitNumber}: ${unitError.message}`);
                }
              }
            } catch (groupError) {
              results.failed += groupData.units.length;
              results.errors.push(`Failed to create group ${groupData.group.name}: ${groupError.message}`);
            }
            processed++;
          }

          setUploadProgress(100);
          setUploadResults(results);
          
          if (results.successful > 0) {
            setSuccess(`Successfully imported ${results.successful} properties!`);
          }

        } catch (error) {
          setError(`Error processing file: ${error.message}`);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      setError(`Error reading file: ${error.message}`);
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Download the sample CSV file from public folder
    const a = document.createElement('a');
    a.href = '/sample_property_import.csv';
    a.download = 'property_import_template.csv';
    a.click();
  };

  const clearFile = () => {
    setFile(null);
    setPreviewData([]);
    setUploadResults(null);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Bulk Import Properties</h2>
          <p className="text-muted mb-0">Upload CSV file to import multiple properties at once</p>
        </div>
        <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Dashboard
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{error}</pre>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUpload} className="text-primary me-2" />
                Upload CSV File
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center p-4 border-2 border-dashed border-secondary rounded">
                <FontAwesomeIcon icon={faFileCsv} className="text-muted mb-3" size="3x" />
                <h5>Choose a CSV file to upload</h5>
                <p className="text-muted mb-3">
                  Select a CSV file containing property data. Make sure it follows the required format.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Choose File
                </Button>
                <Button variant="outline-primary" onClick={downloadTemplate}>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download Template
                </Button>
              </div>

              {file && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Selected File:</strong> {file.name}
                      <br />
                      <small className="text-muted">
                        Size: {(file.size / 1024).toFixed(2)} KB
                      </small>
                    </div>
                    <Button variant="outline-danger" size="sm" onClick={clearFile}>
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      Clear
                    </Button>
                  </div>
                </div>
              )}

              {loading && (
                <div className="mt-3">
                  <ProgressBar 
                    now={uploadProgress} 
                    label={`${uploadProgress}%`}
                    className="mb-2"
                  />
                  <small className="text-muted">Processing properties...</small>
                </div>
              )}

              {file && !loading && (
                <div className="mt-3">
                  <Button
                    variant="success"
                    onClick={handleUpload}
                    disabled={!file}
                    className="w-100"
                  >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    Import Properties
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          {uploadResults && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">Import Results</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="text-center">
                      <div className="bg-success bg-opacity-10 p-3 rounded mb-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="2x" />
                      </div>
                      <h4 className="text-success">{uploadResults.successful}</h4>
                      <p className="text-muted mb-0">Successfully Imported</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <div className="bg-danger bg-opacity-10 p-3 rounded mb-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" size="2x" />
                      </div>
                      <h4 className="text-danger">{uploadResults.failed}</h4>
                      <p className="text-muted mb-0">Failed</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <div className="bg-primary bg-opacity-10 p-3 rounded mb-2">
                        <FontAwesomeIcon icon={faFileCsv} className="text-primary" size="2x" />
                      </div>
                      <h4>{uploadResults.total}</h4>
                      <p className="text-muted mb-0">Total Records</p>
                    </div>
                  </Col>
                </Row>

                {uploadResults.errors.length > 0 && (
                  <div className="mt-3">
                    <h6>Errors:</h6>
                    <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {uploadResults.errors.map((error, index) => (
                        <div key={index} className="text-danger small mb-1">
                          • {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">CSV Format Guide</h5>
            </Card.Header>
            <Card.Body>
              <h6>Required Fields:</h6>
              <ul className="small">
                <li><strong>groupName:</strong> Property group name</li>
                <li><strong>groupType:</strong> tower or community</li>
                <li><strong>groupLocation:</strong> Location/address</li>
                <li><strong>unitNumber:</strong> Unit identifier</li>
                <li><strong>unitType:</strong> apartment, villa, penthouse, studio, or townhouse</li>
              </ul>

              <h6>Optional Fields:</h6>
              <ul className="small">
                <li><strong>bedrooms:</strong> Number of bedrooms</li>
                <li><strong>bathrooms:</strong> Number of bathrooms</li>
                <li><strong>area:</strong> Area in sq ft</li>
                <li><strong>floor:</strong> Floor number</li>
                <li><strong>status:</strong> available, occupied, maintenance, or reserved</li>
                <li><strong>description:</strong> Property description</li>
              </ul>

              <Button variant="outline-primary" size="sm" onClick={downloadTemplate} className="w-100">
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Template
              </Button>
            </Card.Body>
          </Card>

          {previewData.length > 0 && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">Data Preview</h5>
              </Card.Header>
              <Card.Body>
                <div className="small">
                  <strong>Showing first {previewData.length} rows:</strong>
                </div>
                <div className="mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        {Object.keys(previewData[0] || {}).map(header => (
                          <th key={header} className="small">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value, i) => (
                            <td key={i} className="small">{value}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BulkImport; 